import type { ApiClient } from '../../../shared/api/api-client';
import type {
  CreateTaskBody,
  PatchTaskBody,
  TaskAssignee,
  TaskBoard,
  TaskCard,
  TaskHistoryEvent,
  TasksColumn,
  UpdateColumnBody
} from './tasks.types';

function asRecord(raw: unknown): Record<string, unknown> | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  return raw as Record<string, unknown>;
}

function asNumber(raw: unknown): number | null {
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) ? n : null;
}

function normalizeColumn(raw: unknown): TasksColumn | null {
  const r = asRecord(raw);
  if (!r) return null;
  const id = asNumber(r.id);
  const name = typeof r.name === 'string' ? r.name.trim() : '';
  const sortOrder = asNumber(r.sort_order) ?? 0;
  if (id == null || id < 1 || !name) return null;
  return { id, name, sort_order: sortOrder };
}

function normalizeCard(raw: unknown): TaskCard | null {
  const r = asRecord(raw);
  if (!r) return null;
  const id = asNumber(r.id);
  const columnId = asNumber(r.column_id);
  const title = typeof r.title === 'string' ? r.title.trim() : '';
  if (id == null || id < 1 || columnId == null || columnId < 1 || !title) return null;
  const assignee = asNumber(r.assignee_user_id);
  return {
    id,
    column_id: columnId,
    title,
    description: typeof r.description === 'string' ? r.description : '',
    assignee_user_id: assignee != null && assignee > 0 ? assignee : null,
    assignee_name: typeof r.assignee_name === 'string' ? r.assignee_name : '',
    sort_order: asNumber(r.sort_order) ?? 0
  };
}

function normalizeAssignee(raw: unknown): TaskAssignee | null {
  const r = asRecord(raw);
  if (!r) return null;
  const id = asNumber(r.id);
  if (id == null || id < 1) return null;
  return {
    id,
    full_name: typeof r.full_name === 'string' ? r.full_name : '',
    email: typeof r.email === 'string' ? r.email : '',
    role: typeof r.role === 'string' ? r.role : ''
  };
}

function normalizeEvent(raw: unknown): TaskHistoryEvent | null {
  const r = asRecord(raw);
  if (!r) return null;
  const id = asNumber(r.id);
  const eventType = typeof r.event_type === 'string' ? r.event_type : '';
  if (id == null || id < 1 || !eventType) return null;
  const actorId = asNumber(r.actor_user_id);
  const details = asRecord(r.details) ?? {};
  return {
    id,
    event_type: eventType,
    actor_user_id: actorId != null && actorId > 0 ? actorId : null,
    actor_name: typeof r.actor_name === 'string' ? r.actor_name : '',
    details,
    created_at: typeof r.created_at === 'string' ? r.created_at : ''
  };
}

export async function fetchTaskBoard(client: ApiClient): Promise<TaskBoard> {
  const raw = await client.request<unknown>('/tasks/board');
  const r = asRecord(raw);
  const columnsRaw = r && Array.isArray(r.columns) ? r.columns : [];
  const cardsRaw = r && Array.isArray(r.cards) ? r.cards : [];
  const columns: TasksColumn[] = [];
  for (const row of columnsRaw) {
    const c = normalizeColumn(row);
    if (c) columns.push(c);
  }
  const cards: TaskCard[] = [];
  for (const row of cardsRaw) {
    const c = normalizeCard(row);
    if (c) cards.push(c);
  }
  columns.sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);
  cards.sort((a, b) => a.column_id - b.column_id || a.sort_order - b.sort_order || a.id - b.id);
  return { columns, cards };
}

export async function fetchTaskAssignees(client: ApiClient): Promise<TaskAssignee[]> {
  const raw = await client.request<unknown>('/tasks/assignees');
  const r = asRecord(raw);
  const itemsRaw = r && Array.isArray(r.items) ? r.items : [];
  const out: TaskAssignee[] = [];
  for (const row of itemsRaw) {
    const a = normalizeAssignee(row);
    if (a) out.push(a);
  }
  return out;
}

export async function createTaskColumn(client: ApiClient, name: string): Promise<number> {
  const raw = await client.request<unknown>('/tasks/columns', {
    method: 'POST',
    body: { name }
  });
  const id = asNumber(asRecord(raw)?.id);
  if (id == null || id < 1) throw new Error('Некорректный ответ сервера');
  return id;
}

export async function updateTaskColumn(
  client: ApiClient,
  columnId: number,
  body: UpdateColumnBody
): Promise<void> {
  await client.request(`/tasks/columns/${columnId}`, { method: 'PATCH', body });
}

export async function deleteTaskColumn(client: ApiClient, columnId: number): Promise<void> {
  await client.request(`/tasks/columns/${columnId}`, { method: 'DELETE' });
}

export async function createTask(client: ApiClient, body: CreateTaskBody): Promise<number> {
  const raw = await client.request<unknown>('/tasks/cards', { method: 'POST', body });
  const id = asNumber(asRecord(raw)?.id);
  if (id == null || id < 1) throw new Error('Некорректный ответ сервера');
  return id;
}

export async function fetchTask(client: ApiClient, cardId: number): Promise<TaskCard> {
  const raw = await client.request<unknown>(`/tasks/cards/${cardId}`);
  const card = normalizeCard(raw);
  if (!card) throw new Error('Некорректный ответ сервера');
  return card;
}

export async function patchTask(
  client: ApiClient,
  cardId: number,
  body: PatchTaskBody
): Promise<TaskCard> {
  const raw = await client.request<unknown>(`/tasks/cards/${cardId}`, { method: 'PATCH', body });
  const card = normalizeCard(raw);
  if (!card) throw new Error('Некорректный ответ сервера');
  return card;
}

export async function deleteTask(client: ApiClient, cardId: number): Promise<void> {
  await client.request(`/tasks/cards/${cardId}`, { method: 'DELETE' });
}

export async function fetchTaskHistory(
  client: ApiClient,
  cardId: number,
  params: { limit?: number; offset?: number } = {}
): Promise<{ items: TaskHistoryEvent[]; total: number }> {
  const sp = new URLSearchParams();
  sp.set('limit', String(params.limit ?? 50));
  sp.set('offset', String(params.offset ?? 0));
  const raw = await client.request<unknown>(`/tasks/cards/${cardId}/history?${sp.toString()}`);
  const r = asRecord(raw);
  const itemsRaw = r && Array.isArray(r.items) ? r.items : [];
  const items: TaskHistoryEvent[] = [];
  for (const row of itemsRaw) {
    const e = normalizeEvent(row);
    if (e) items.push(e);
  }
  const totalRaw = asNumber(r?.total);
  return { items, total: totalRaw != null && totalRaw >= 0 ? totalRaw : items.length };
}
